import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, Copy, Check, KeyRound, Loader2, RefreshCw, Mail } from "lucide-react";

/**
 * Second-factor gate shown right after the password step.
 *  mode="setup"  → enrol an authenticator app, then reveal one-time backup codes
 *  mode="verify" → enter the 6-digit code (or a backup code)
 */
export default function MfaChallenge({ mfaToken, mode, name, onSuccess, onCancel }) {
  const [stage, setStage] = useState(mode === "setup" ? "enrol" : "code");
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [pending, setPending] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [method, setMethod] = useState(mode === "setup" ? "totp" : null);
  const [emailInfo, setEmailInfo] = useState(null);
  const started = useRef(false);

  const sendEmailCode = useCallback(async (url) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(url, { mfa_token: mfaToken });
      if (data.method) setMethod(data.method);
      if (data.sent) setEmailInfo(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [mfaToken]);

  const startSetup = useCallback(async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { mfa_token: mfaToken });
      setSetupData(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [mfaToken]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (mode === "setup") startSetup();
    else sendEmailCode("/auth/mfa/challenge/start");
  }, [mode, startSetup, sendEmailCode]);

  const chooseEmailSetup = () => {
    setMethod("email"); setCode("");
    sendEmailCode("/auth/mfa/email/setup");
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const url = mode !== "setup" ? "/auth/mfa/verify"
        : method === "email" ? "/auth/mfa/email/setup/confirm" : "/auth/mfa/setup/confirm";
      const { data } = await api.post(url, { mfa_token: mfaToken, code });
      if (data.recovery_codes?.length) {
        setRecovery(data.recovery_codes);
        setPending(data);
        setStage("codes");
      } else {
        onSuccess(data);
      }
    } catch (e) {
      setErr(formatApiError(e));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const submitBackup = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/recovery", { mfa_token: mfaToken, code: backup });
      onSuccess(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recovery.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Copy failed — please write the codes down manually.");
    }
  };

  // ─── backup codes reveal (shown once) ───────────────────────────────────
  if (stage === "codes") {
    return (
      <div className="space-y-5 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-recovery-codes">
        <header>
          <div className="flex items-center gap-2 text-[var(--dojo-green)] mb-2">
            <ShieldCheck size={16} />
            <span className="text-[10px] uppercase tracking-[0.24em]">Two-factor is now active</span>
          </div>
          <h2 className="font-serif text-3xl tracking-tight">Save your backup codes</h2>
          <p className="text-sm text-[var(--dojo-ink-soft)] mt-2">
            Each code works once, and this is the only time they are shown. Keep them somewhere safe —
            they are the way back in if you lose your phone.
          </p>
        </header>
        <ul className="grid grid-cols-2 gap-2 font-mono-accent text-sm">
          {recovery.map((c) => (
            <li key={c} className="border border-[var(--dojo-border)] px-3 py-2 tracking-widest text-center">{c}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={copyCodes} className="btn-outline flex items-center gap-2" data-testid="mfa-copy-codes-btn">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy all"}
          </button>
          <button type="button" onClick={() => onSuccess(pending)} className="btn-primary flex-1" data-testid="mfa-codes-continue-btn">
            I've saved them — continue
          </button>
        </div>
        {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="mfa-error">{err}</div>}
      </div>
    );
  }

  // ─── backup-code sign-in ────────────────────────────────────────────────
  if (stage === "backup") {
    return (
      <form onSubmit={submitBackup} className="space-y-5 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-backup-form">
        <header>
          <div className="flex items-center gap-2 text-[var(--dojo-ink-soft)] mb-2">
            <KeyRound size={16} />
            <span className="text-[10px] uppercase tracking-[0.24em]">Backup code</span>
          </div>
          <h2 className="font-serif text-3xl tracking-tight">Use a one-time code</h2>
          <p className="text-sm text-[var(--dojo-ink-soft)] mt-2">
            Enter one of the backup codes issued when you set up two-factor. It will be consumed after use.
          </p>
        </header>
        <input
          autoFocus
          value={backup}
          onChange={(e) => setBackup(e.target.value.toUpperCase())}
          placeholder="XXXXX-XXXXX"
          data-testid="mfa-backup-input"
          className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 font-mono-accent tracking-[0.3em] text-center focus:outline-none focus:border-[var(--dojo-ink)]"
        />
        {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="mfa-error">{err}</div>}
        <button type="submit" disabled={busy || backup.replace(/[^A-Z0-9]/g, "").length < 8} className="btn-primary w-full" data-testid="mfa-backup-submit">
          {busy ? "Checking…" : "Sign in with backup code"}
        </button>
        <button type="button" onClick={() => { setStage("code"); setErr(""); }} className="ink-underline text-sm text-[var(--dojo-ink-soft)]" data-testid="mfa-use-app-btn">
          {method === "email" ? "Back to email code" : "Use my authenticator app instead"}
        </button>
      </form>
    );
  }

  // ─── enrolment by email code ────────────────────────────────────────────
  if (mode === "setup" && method === "email") {
    return (
      <EmailCodeForm
        title="Use email codes"
        intro={emailInfo ? `We sent a 6-digit code to ${emailInfo.email}. Enter it to switch on two-factor.` : "Sending a code to your email…"}
        code={code} setCode={setCode} err={err} busy={busy}
        onSubmit={submitCode} submitLabel="Activate two-factor" submitDisabled={!emailInfo}
        onResend={() => sendEmailCode("/auth/mfa/email/setup")}
        footer={
          <button type="button" onClick={() => { setMethod("totp"); setErr(""); setCode(""); }} className="ink-underline text-sm text-[var(--dojo-ink-soft)]" data-testid="mfa-use-app-setup-btn">
            Use an authenticator app instead
          </button>
        }
      />
    );
  }

  // ─── enrolment ──────────────────────────────────────────────────────────
  if (mode === "setup") {
    return (
      <div className="space-y-6 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-setup-form">
        <header>
          <div className="flex items-center gap-2 text-[var(--dojo-hinomaru)] mb-2">
            <ShieldCheck size={16} />
            <span className="text-[10px] uppercase tracking-[0.24em]">Required · One-time setup</span>
          </div>
          <h2 className="font-serif text-3xl tracking-tight">Protect your account</h2>
          <p className="text-sm text-[var(--dojo-ink-soft)] mt-2">
            {name ? `${name}, ` : ""}the dojo requires a second factor. Scan the square below with
            Google Authenticator, Microsoft Authenticator, Authy or 1Password, then enter the 6-digit code it shows.
          </p>
        </header>

        {busy && !setupData && (
          <div className="flex items-center gap-2 text-sm text-[var(--dojo-ink-soft)]">
            <Loader2 size={14} className="animate-spin" /> Preparing your authenticator…
          </div>
        )}

        {setupData && (
          <div className="grid sm:grid-cols-[180px_1fr] gap-6 items-start">
            <img
              src={setupData.qr_png}
              alt="Authenticator QR code"
              className="w-[180px] h-[180px] border border-[var(--dojo-border)] bg-white"
              data-testid="mfa-qr-image"
            />
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-1">Can't scan? Enter this key</div>
                <code className="block break-all font-mono-accent text-xs border border-[var(--dojo-border)] px-3 py-2" data-testid="mfa-manual-key">
                  {setupData.manual_key}
                </code>
              </div>
              <div className="text-[11px] text-[var(--dojo-ink-soft)] leading-relaxed">
                Account: <span className="text-[var(--dojo-ink)]">{setupData.account_label}</span><br />
                Issuer: <span className="text-[var(--dojo-ink)]">{setupData.issuer}</span>
              </div>
              <button type="button" onClick={startSetup} className="ink-underline text-xs text-[var(--dojo-ink-soft)] !inline-flex items-center gap-1.5" data-testid="mfa-regenerate-secret-btn">
                <RefreshCw size={11} /> Start over with a new key
              </button>
            </div>
          </div>
        )}

        <form onSubmit={submitCode} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-2">6-digit code</label>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="mfa-code-input"
              placeholder="000000"
              className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 font-mono-accent text-2xl tracking-[0.5em] text-center focus:outline-none focus:border-[var(--dojo-ink)]"
            />
          </div>
          {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="mfa-error">{err}</div>}
          <button type="submit" disabled={busy || code.length !== 6 || !setupData} className="btn-primary w-full" data-testid="mfa-setup-submit">
            {busy ? "Verifying…" : "Activate two-factor"}
          </button>
        </form>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--dojo-ink-soft)]">
          <button type="button" onClick={chooseEmailSetup} className="ink-underline !inline-flex items-center gap-1.5 text-left" data-testid="mfa-use-email-setup-btn">
            <Mail size={13} /> No authenticator app? Get codes by email
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="ink-underline whitespace-nowrap" data-testid="mfa-cancel-btn">
              Back to sign in
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── verification by email code ─────────────────────────────────────────
  if (method === "email") {
    return (
      <EmailCodeForm
        title="Check your email"
        intro={`${name ? `Welcome back, ${name.split(" ")[0]}. ` : ""}${emailInfo ? `We sent a 6-digit code to ${emailInfo.email}. It expires in ${emailInfo.expires_in_minutes} minutes.` : "Sending your code…"}`}
        code={code} setCode={setCode} err={err} busy={busy}
        onSubmit={submitCode} submitLabel="Verify and sign in" submitDisabled={false}
        onResend={() => sendEmailCode("/auth/mfa/challenge/start")}
        footer={
          <div className="flex items-center justify-between text-sm text-[var(--dojo-ink-soft)]">
            <button type="button" onClick={() => { setStage("backup"); setErr(""); }} className="ink-underline" data-testid="mfa-use-backup-btn">
              Can't get the email? Use a backup code
            </button>
            {onCancel && <button type="button" onClick={onCancel} className="ink-underline" data-testid="mfa-cancel-btn">Cancel</button>}
          </div>
        }
      />
    );
  }

  if (!method) {
    return (
      <div className="space-y-4 text-sm text-[var(--dojo-ink-soft)] border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-loading">
        {!err ? (
          <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Preparing verification…</div>
        ) : (
          <>
            <div className="text-[var(--dojo-hinomaru)]" data-testid="mfa-error">{err}</div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button type="button" onClick={() => sendEmailCode("/auth/mfa/challenge/start")} disabled={busy} className="ink-underline !inline-flex items-center gap-1.5" data-testid="mfa-retry-btn">
                <RefreshCw size={11} /> Try again
              </button>
              <button type="button" onClick={() => { setStage("backup"); setErr(""); }} className="ink-underline" data-testid="mfa-use-backup-btn">
                Use a backup code
              </button>
              {onCancel && <button type="button" onClick={onCancel} className="ink-underline" data-testid="mfa-cancel-btn">Cancel</button>}
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── verification ───────────────────────────────────────────────────────
  return (
    <form onSubmit={submitCode} className="space-y-5 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-verify-form">
      <header>
        <div className="flex items-center gap-2 text-[var(--dojo-green)] mb-2">
          <ShieldCheck size={16} />
          <span className="text-[10px] uppercase tracking-[0.24em]">Two-factor required</span>
        </div>
        <h2 className="font-serif text-3xl tracking-tight">Enter your code</h2>
        <p className="text-sm text-[var(--dojo-ink-soft)] mt-2">
          {name ? `Welcome back, ${name.split(" ")[0]}. ` : ""}Open your authenticator app and type the current 6-digit code.
        </p>
      </header>
      <input
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        data-testid="mfa-code-input"
        placeholder="000000"
        className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 font-mono-accent text-2xl tracking-[0.5em] text-center focus:outline-none focus:border-[var(--dojo-ink)]"
      />
      {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="mfa-error">{err}</div>}
      <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full" data-testid="mfa-verify-submit">
        {busy ? "Verifying…" : "Verify and sign in"}
      </button>
      <div className="flex items-center justify-between text-sm text-[var(--dojo-ink-soft)] pt-1">
        <button type="button" onClick={() => { setStage("backup"); setErr(""); }} className="ink-underline" data-testid="mfa-use-backup-btn">
          Lost your phone? Use a backup code
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="ink-underline" data-testid="mfa-cancel-btn">Cancel</button>
        )}
      </div>
    </form>
  );
}

function EmailCodeForm({ title, intro, code, setCode, err, busy, onSubmit, submitLabel, submitDisabled, onResend, footer }) {
  return (
    <form onSubmit={onSubmit} className="space-y-5 border border-[var(--dojo-border)] bg-[var(--dojo-paper)] p-8" data-testid="mfa-email-form">
      <header>
        <div className="flex items-center gap-2 text-[var(--dojo-green)] mb-2">
          <Mail size={16} />
          <span className="text-[10px] uppercase tracking-[0.24em]">Email code</span>
        </div>
        <h2 className="font-serif text-3xl tracking-tight">{title}</h2>
        <p className="text-sm text-[var(--dojo-ink-soft)] mt-2" data-testid="mfa-email-intro">{intro}</p>
      </header>
      <input
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        data-testid="mfa-code-input"
        placeholder="000000"
        className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 font-mono-accent text-2xl tracking-[0.5em] text-center focus:outline-none focus:border-[var(--dojo-ink)]"
      />
      {err && <div className="text-[var(--dojo-hinomaru)] text-sm" data-testid="mfa-error">{err}</div>}
      <button type="submit" disabled={busy || code.length !== 6 || submitDisabled} className="btn-primary w-full" data-testid="mfa-email-submit">
        {busy ? "Working…" : submitLabel}
      </button>
      <button type="button" onClick={onResend} disabled={busy} className="ink-underline text-xs text-[var(--dojo-ink-soft)] !inline-flex items-center gap-1.5" data-testid="mfa-email-resend-btn">
        <RefreshCw size={11} /> Send a new code
      </button>
      {footer}
    </form>
  );
}
